import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Brand variants — sharp corners, uppercase tracked text, premium hover treatments.
        // gold: white sweeps in from the left on hover via a 200%-wide hard-stop gradient
        gold:
          "rounded-none font-bold uppercase tracking-[0.15em] text-hillpoint border border-gold bg-[linear-gradient(to_right,#ffffff_50%,#D4AF37_50%)] bg-[length:200%_100%] bg-[position:100%_0] hover:bg-[position:0%_0] transition-[background-position,transform] duration-500 shadow-[0_0_30px_rgba(212,175,55,0.2)] hover:scale-[1.02] active:scale-95",
        hillpoint:
          "rounded-none font-bold uppercase tracking-[0.15em] bg-hillpoint text-white border border-hillpoint hover:bg-gold hover:border-gold hover:text-hillpoint transition-all duration-500 shadow-xl hover:shadow-2xl",
        outlineLight:
          "rounded-none font-bold uppercase tracking-[0.15em] bg-transparent border border-white/50 text-white backdrop-blur-sm hover:border-gold hover:text-gold hover:bg-white/10 transition-all duration-300",
        outlineDark:
          "rounded-none font-bold uppercase tracking-[0.15em] bg-transparent border border-hillpoint text-hillpoint hover:bg-hillpoint hover:text-white transition-all duration-300",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        // Brand sizes — no radius or case overrides, so they compose with the brand variants
        xl: "h-auto px-8 py-4 text-base",
        hero: "h-auto px-8 py-4 md:px-12 md:py-6 text-base md:text-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
